import { siteConfig, aboutData } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: siteConfig.name,
        url: siteConfig.url,
        description: siteConfig.description,
        contactPoint: {
          '@type': 'ContactPoint',
          email: aboutData.contact.email,
          contactType: 'customer service',
          availableLanguage: ['Chinese'],
        },
      },
      {
        '@type': 'WebPage',
        name: `关于我们 - ${siteConfig.name}`,
        url: `${siteConfig.url}/about`,
        description: `了解${siteConfig.name}的品牌故事、信誉保障和联系方式`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, item: { '@id': siteConfig.url, name: '首页' } },
          { '@type': 'ListItem', position: 2, item: { '@id': `${siteConfig.url}/about`, name: '关于我们' } },
        ],
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <Navbar />
      <main>
        <section className="pt-24 pb-8 bg-background-100">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              关于我们
            </h1>
            <p className="text-foreground-600 text-sm md:text-base">
              了解亚洲一区二区的品牌故事与发展历程
            </p>
          </div>
        </section>

        <section className="py-16 bg-background-50">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                {aboutData.brandIntro.title}
              </h2>
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-10 max-w-3xl">
                {aboutData.brandIntro.content}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {aboutData.brandIntro.highlights.map((h) => (
                  <div key={h.label} className="bg-background-100 rounded-lg p-6 text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">{h.value}</div>
                    <div className="text-sm text-foreground-500">{h.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-16">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                {aboutData.reputation.title}
              </h2>
              <p className="text-foreground-600 text-sm mb-8">{aboutData.reputation.intro}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {aboutData.reputation.badges.map((badge) => (
                  <div key={badge.title} className="bg-background-100 rounded-lg p-6 flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-shield-check-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{badge.title}</h3>
                      <p className="text-foreground-500 text-xs leading-relaxed">{badge.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                {aboutData.contact.title}
              </h2>
              <p className="text-foreground-600 text-sm mb-8">{aboutData.contact.intro}</p>
              <div className="bg-background-100 rounded-lg p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-1">电子邮箱</div>
                      <div className="text-sm font-medium text-foreground-900">{aboutData.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-1">微信公众号</div>
                      <div className="text-sm font-medium text-foreground-900">{aboutData.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-1">地址</div>
                      <div className="text-sm font-medium text-foreground-900">{aboutData.contact.address}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-background-200 text-foreground-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-400 mb-1">服务时间</div>
                      <div className="text-sm font-medium text-foreground-900">{aboutData.contact.workingHours}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}